/**
 * The sample call as a scroll timeline. Scroll progress (0..1) across the pinned stage maps
 * onto five lines of `call.dialogue`; each word gets its own small window, weighted by its
 * length, so the captions fill like a person speaking rather than a metronome.
 *
 * Everything here is pure and computed once from the content.
 *
 * @module
 */
import { call, type DialogueLine } from '@/content/site'

/** A window in normalised scroll progress (0..1). */
export interface Cue {
  readonly start: number
  readonly end: number
}

/** One dialogue line on the timeline. */
export interface LineCue extends Cue {
  readonly speaker: DialogueLine['speaker']
  /** Words in display order (the romanised Hindi split on spaces). */
  readonly tokens: readonly string[]
  /** One cue per token: where that word rises from ghost to full ink. */
  readonly words: readonly Cue[]
  /** The stretch in which this speaker is audibly talking (drives waveform and orb). */
  readonly speech: Cue
  /** When the English subtitle fades in under the line. */
  readonly subtitle: Cue
}

/** A task chip: when its spinner appears, and when it resolves to a check. */
export interface ChipCue {
  readonly pending: number
  readonly done: number
}

/** The whole call. */
export interface CallTimeline {
  readonly lines: readonly LineCue[]
  /** `focus` (a float line index) eases from i to i + 1 inside `transitions[i]`. */
  readonly transitions: readonly Cue[]
  readonly chips: readonly ChipCue[]
  /** Progress where the first word starts: before it, the call is still connecting. */
  readonly firstWord: number
}

// Units are arbitrary "beats"; everything is normalised at the end.
const LEAD = 1.3 // the call has just connected: the orb rings before anyone speaks
const GAP = 0.55 // a breath between lines
const OUTRO = 2.4 // the last task resolves and the card holds
const LINE_BASE = 0.9 // every line takes at least this long
const PER_CHAR = 0.042 // …plus this per character
const WORD_RISE = 0.62 // how long one word takes to rise; several overlap

/**
 * Which line makes each task true, as `[line index, fraction of that line's length]`.
 * Fractions above 1 run into the gap or the outro. Matches `call.tasks`:
 *   0 "Calling 3 customers": the owner says yes (line 2); done while Vokarta replies (line 3)
 *   1 "Offer post ready":    Vokarta drafts it during line 3
 *   2 "Posted to Instagram": the owner's final yes (line 4); done as the call winds down
 * Extra tasks (if the content grows) resolve evenly through the outro.
 */
const TASK_CUES: readonly (readonly [
  pending: readonly [number, number],
  done: readonly [number, number],
])[] = [
  [
    [2, 0.32],
    [3, 0.22],
  ],
  [
    [3, 0.3],
    [3, 0.72],
  ],
  [
    [4, 0.95],
    [4, 2.1],
  ],
]

function buildTimeline(dialogue: readonly DialogueLine[], taskCount: number): CallTimeline {
  let cursor = LEAD
  const raw = dialogue.map((line) => {
    const tokens = line.hi.split(/\s+/).filter(Boolean)
    const length = LINE_BASE + line.hi.length * PER_CHAR
    const start = cursor
    const end = start + length
    cursor = end + GAP

    const speechStart = start + length * 0.05
    const speechEnd = start + length * 0.88
    // A word's weight is its length plus a beat for the space, so short words still take time.
    const weights = tokens.map((token) => token.length + 1.5)
    const before = weights.reduce((sum, weight) => sum + weight, 0) - (weights.at(-1) ?? 0)
    const spread = Math.max(speechEnd - WORD_RISE - speechStart, 0)
    let accumulated = 0
    const words = tokens.map((_, index) => {
      const wordStart = speechStart + (before > 0 ? accumulated / before : 0) * spread
      accumulated += weights[index]
      return { start: wordStart, end: wordStart + WORD_RISE }
    })
    const quarter = words[Math.floor(tokens.length * 0.2)] ?? words[0]
    const half = words[Math.floor(tokens.length * 0.55)] ?? words[0]
    return {
      speaker: line.speaker,
      tokens,
      start,
      end,
      length,
      words,
      speech: { start: speechStart, end: speechEnd },
      subtitle: { start: quarter.start, end: half.end },
    }
  })

  const total = cursor - GAP + OUTRO
  const n = (value: number) => value / total
  const cue = ({ start, end }: Cue): Cue => ({ start: n(start), end: n(end) })

  const lines: LineCue[] = raw.map((line) => ({
    speaker: line.speaker,
    tokens: line.tokens,
    start: n(line.start),
    end: n(line.end),
    words: line.words.map(cue),
    speech: cue(line.speech),
    subtitle: cue(line.subtitle),
  }))

  // The captions glide to the next line across the breath between two lines.
  const transitions = raw.slice(0, -1).map((line, index) => ({
    start: n(line.end - 0.65),
    end: n(raw[index + 1].start + 0.65),
  }))

  const at = ([lineIndex, fraction]: readonly [number, number]) => {
    const line = raw[Math.min(lineIndex, raw.length - 1)]
    return n(line.start + line.length * fraction)
  }
  const lastEnd = raw.at(-1)?.end ?? cursor
  const chips = Array.from({ length: taskCount }, (_, index) => {
    const cueSpec = TASK_CUES[index]
    if (cueSpec) return { pending: at(cueSpec[0]), done: at(cueSpec[1]) }
    const slot = (index + 1) / (taskCount + 1)
    return { pending: n(lastEnd + OUTRO * slot * 0.6), done: n(lastEnd + OUTRO * slot * 0.9) }
  })

  return { lines, transitions, chips, firstWord: lines[0]?.words[0]?.start ?? 0 }
}

/** The sample call's timeline, built once from `call.dialogue` and `call.tasks`. */
export const CALL_TIMELINE: CallTimeline = buildTimeline(call.dialogue, call.tasks.length)

/** Approximate spoken length of the call in seconds (for the still timer under reduced motion). */
export const CALL_SECONDS: number = Math.round(
  call.dialogue.reduce((sum, line) => sum + line.hi.split(/\s+/).length, 0) / 2.3,
)
